// status-effective-config-readers.ts
//
// Split out of status-renderer.ts (P13 Agent C — file crossed the 200-line
// budget once the operating-table work landed on top of Agent B's cost/limits
// work). Two tiny best-effort readers of `<control_root>/effective-config.json`
// (P4's already-written snapshot — see effective-config-loader.ts) used only
// by status-renderer.ts's payload builders: missing/unreadable file is never
// fatal for a read-only `status` command, it just means "show unlimited"/
// "show no ceiling" rather than crash.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { EFFECTIVE_CONFIG_V2_FILE_NAME } from "./effective-config-v2-loader.js";
import type { CostAttemptLimitsConfig } from "./mission-cost-accounting.js";
import {
  DEFAULT_MAX_ATTEMPTS_PER_MISSION,
  DEFAULT_MAX_COST_USD_PER_MISSION,
} from "./schemas/config-budget-ceilings.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import { AutopilotConfigV2Schema } from "./schemas/config-v2-schema.js";

// 06g R5: the schema's own defaults ($25 / 60) — never a local 0.
const DEFAULT_LIMITS: CostAttemptLimitsConfig = {
  max_cost_usd_per_mission: DEFAULT_MAX_COST_USD_PER_MISSION,
  max_attempts_per_mission: DEFAULT_MAX_ATTEMPTS_PER_MISSION,
};

/** Reads the two ceiling keys from effective-config.json; missing/unreadable => the schema defaults. */
export async function readLimitsFromEffectiveConfig(
  controlRoot: string,
): Promise<CostAttemptLimitsConfig> {
  try {
    const raw = await fs.readFile(join(controlRoot, "effective-config.json"), "utf8");
    const parsed = JSON.parse(raw) as Partial<CostAttemptLimitsConfig>;
    return {
      max_cost_usd_per_mission:
        typeof parsed.max_cost_usd_per_mission === "number"
          ? parsed.max_cost_usd_per_mission
          : DEFAULT_LIMITS.max_cost_usd_per_mission,
      max_attempts_per_mission:
        typeof parsed.max_attempts_per_mission === "number"
          ? parsed.max_attempts_per_mission
          : DEFAULT_LIMITS.max_attempts_per_mission,
    };
  } catch {
    return DEFAULT_LIMITS;
  }
}

/** max_counted_failures from effective-config.json — best-effort, undefined when unreadable. */
export async function readMaxCountedFailures(controlRoot: string): Promise<number | undefined> {
  try {
    const raw = await fs.readFile(join(controlRoot, "effective-config.json"), "utf8");
    const parsed = JSON.parse(raw) as Partial<AutopilotConfig>;
    return typeof parsed.max_counted_failures === "number"
      ? parsed.max_counted_failures
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * V2 §7: `max_auto_decisions_per_mission` from the run's V2 snapshot (E12) —
 * a file change takes effect at the next run, never mid-run. Missing (mission
 * last run by a v1 CLI) or unreadable snapshot ⇒ the schema default.
 */
export async function readMaxAutoDecisionsFromEffectiveConfigV2(
  controlRoot: string,
): Promise<number> {
  try {
    const raw = await fs.readFile(join(controlRoot, EFFECTIVE_CONFIG_V2_FILE_NAME), "utf8");
    return AutopilotConfigV2Schema.parse(JSON.parse(raw)).max_auto_decisions_per_mission;
  } catch {
    return AutopilotConfigV2Schema.parse({}).max_auto_decisions_per_mission;
  }
}
